import { useEffect, useState } from "react";
import { getOccurrenceStepKeyOfValue, getOccurrenceStepLabelByStepValue, OccurrenceStepEnum, OccurrenceStepLabelEnum } from "../../../../enums/occurrenceStep";
import RadioButton from "@rio-cloud/rio-uikit/RadioButton";
import { OccurrenceReview, OccurrenceStep } from "../../../../models/occurrence";
import SuccessDialog from "../../../../components/dialogs/SuccessDialog";
import ErrorDialog from "../../../dialogs/ErrorDialog";

interface AssessmentFormProps {
  occurrenceId: string;
  defaultStep: OccurrenceStepEnum;
  review: OccurrenceReview[];
  reviewStep: (occurrenceId: string, step: OccurrenceStepEnum, rate: number, comment?: string) => Promise<void>;
  steps: OccurrenceStep[];
}

const AssessmentForm = ({
  occurrenceId,
  review,
  reviewStep,
  defaultStep,
  steps,
}: AssessmentFormProps) => {
  const [step, setStep] = useState<OccurrenceStepEnum>(defaultStep);

  const [showSuccessDialog, setShowSuccessDialog] = useState<boolean>(false);
  const [showErrorDialog, setShowErrorDialog] = useState<boolean>(false);

  const [rate, setRate] = useState<number | null>(null);
  const [comment, setComment] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    const reviewStep = review.find(r => r.step === step);
    setRate(reviewStep?.rate ?? null);
    setComment(reviewStep?.comment ?? null);
  }, [step, review]);

  const handleSkipStep = async () => {
    const steps = Object.keys(OccurrenceStepEnum);
    let currentStep = step;
    let availableNextStep = false;
    while (!availableNextStep) {
      const stepIndex = steps.findIndex(s => s === getOccurrenceStepKeyOfValue(currentStep));
      const nextStep = steps[stepIndex + 1];
      if (hasStepFinished(OccurrenceStepEnum[nextStep])) {
        currentStep = OccurrenceStepEnum[nextStep];
      } else {
        currentStep = OccurrenceStepEnum[nextStep];
        availableNextStep = true;
      }
    }
    setStep(currentStep);
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      await reviewStep(occurrenceId, step, rate ?? 0, comment ?? "");
      setShowSuccessDialog(true);
    } catch (error) {
      setShowErrorDialog(true);
    } finally {
      setIsLoading(false);
    }
  };

  const hasStepFinished = (step: OccurrenceStepEnum): boolean => {
    return !steps.some(s => s.step === step && s.dtEnd);
  }

  return (
    <div className="p-4 bg-white dark:bg-neutral-900">
      <h2 className="text-size-h4 font-bold">Avaliação</h2>
      <p className="text-color-status-resting text-size-14">
        Avalie etapas concluídas da sua manutenção
      </p>

      <div className="w-[96%] pt-[30px]">
        <ul className="nav nav-tabs nav-justified">
          {Object.keys(OccurrenceStepEnum)
            .slice(0, 8)
            .map((childStep) => (
              <li
                key={childStep}
                className={`nav-item ${OccurrenceStepEnum[childStep] === step ? "active" : ""} ${hasStepFinished(OccurrenceStepEnum[childStep]) ? "disabled" : ""}`}
              >
                <a
                  href="#"
                  className="cursor-pointer"
                  onClick={() => setStep(OccurrenceStepEnum[childStep])}
                >
                  {OccurrenceStepLabelEnum[childStep]}
                </a>
              </li>
            ))}
        </ul>

        <div className="h-[10px] w-full" />

        <ul className="nav nav-tabs nav-justified mt-4">
          {Object.keys(OccurrenceStepEnum)
            .slice(8)
            .map((childStep) => (
              <li
                key={childStep}
                className={`nav-item ${OccurrenceStepEnum[childStep] === step ? "active" : ""} ${hasStepFinished(OccurrenceStepEnum[childStep]) ? "disabled" : ""}`}
              >
                <a
                  href="#"
                  className="cursor-pointer"
                  onClick={() => setStep(OccurrenceStepEnum[childStep])}
                >
                  {OccurrenceStepLabelEnum[childStep]}
                </a>
              </li>
            ))}
        </ul>
      </div>

      <div className="mt-[25px] text-[#707F8F] dark:text-neutral-400">
        <span className="text-size-16 font-bold text-color-gray-dark">
          Avalie como foi a etapa de {getOccurrenceStepLabelByStepValue(step)} do seu veículo
        </span>

        <div className="height-10 w-full margin-top-20 rate-bar"></div>

        <div className="margin-top-20 w-full flex justify-between">
          {[...Array(11)].map((_, index) => (
            <label key={index} className="flex items-center gap-5">
              <RadioButton
                name="rating"
                checked={rate === index}
                onChange={() => {
                  if (review.some(r => r.step === step)) return;
                  setRate(index);
                }}
                className="margin-top-0"
                id="1"
                inline
              >
                <span className="text-[14px] text-[#000000] dark:text-neutral-100">{index}</span>
              </RadioButton>
            </label>
          ))}
        </div>

        <div className="form-group margin-top-20">
          <label htmlFor="textArea">Comentários</label>
          <textarea
            disabled={review.some(r => r.step === step && r.comment !== null) || isLoading}
            className="form-control"
            id="textArea"
            placeholder="Descritivo"
            value={comment ?? ""}
            onChange={(e) => setComment(e.target.value)}
            style={{ height: 173 }}
          ></textarea>
        </div>

        {!review.some(r => r.step === step) && <footer className="footer-shadow mt-[100px] p-[20px]">
          <div className="flex justify-between">
            <span className="flex items-center gap-4">
              <span className="rioglyph rioglyph-info flex items-center justify-center bg-primary text-[#FFFFFF] rounded-full w-[20px] h-[20px]"></span>
              <span className="text-[16px] font-[600] text-[#2A3740] dark:text-neutral-100">
                Clique para salvar sua avaliação ou pule esta etapa
              </span>
            </span>
            <div className="flex gap-6">
              <button
                type="button"
                className="btn btn-lg btn-default w-[125px] h-[40px]"
                onClick={handleSkipStep}
              >
                Pular etapa
              </button>
              <button
                type="button"
                className="btn btn-lg btn-primary w-[125px] h-[40px]"
                onClick={handleSave}
                disabled={isLoading || !rate}
              >
                {isLoading ? "Enviando..." : "Salvar"}
              </button>
            </div>
          </div>
        </footer>}
      </div>

      <SuccessDialog
        show={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        title="Avaliação salva com sucesso"
        body="Avaliação salva com sucesso!"
      />

      <ErrorDialog
        show={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        title="Erro ao salvar avaliação"
        body="Erro ao salvar avaliação"
      />
    </div>
  );
};

export default AssessmentForm;
